import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ClipboardList, 
  Search,
  Package, 
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Loader2,
  MapPin,
  Phone,
  Calendar,
  CreditCard,
  ChevronRight,
  Eye,
  Download,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  released: { label: 'Released', color: 'bg-purple-100 text-purple-700', icon: Package },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-cyan-100 text-cyan-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700', icon: XCircle },
};

const PAYMENT_STATUS_CONFIG = {
  unpaid: { label: 'Unpaid', color: 'bg-red-100 text-red-700' },
  partial: { label: 'Partial', color: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
};

const CustomerOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, statusFilter]);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('nas_customer_token');
      if (!token) {
        navigate('/nas-portal/login');
        return;
      }
      
      const response = await axios.get(`${API}/api/nas-customer/orders?token=${token}`);
      setOrders(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/nas-portal/login');
      } else {
        toast.error('Failed to load orders');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(o => 
        o.delivery_code?.toLowerCase().includes(term) ||
        o.items?.some(i => i.product_name?.toLowerCase().includes(term))
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === statusFilter);
    }
    
    setFilteredOrders(filtered);
  };

  const viewOrderDetails = async (order) => {
    try {
      const token = localStorage.getItem('nas_customer_token');
      const response = await axios.get(`${API}/api/nas-customer/orders/${order.id}?token=${token}`);
      setSelectedOrder(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Export orders to CSV
  const exportOrdersCSV = () => {
    if (orders.length === 0) {
      toast.error('No orders to export');
      return;
    }

    const headers = ['Order Code', 'Date', 'Status', 'Payment Status', 'Items', 'Total Amount', 'Delivery Address'];
    
    const rows = orders.map(order => [
      order.delivery_code || '',
      new Date(order.created_at).toLocaleDateString(),
      STATUS_CONFIG[order.status]?.label || order.status,
      PAYMENT_STATUS_CONFIG[order.payment_status]?.label || order.payment_status,
      order.items?.map(i => `${i.product_name} (${i.quantity} ${i.unit})`).join('; ') || '',
      order.total_amount || 0,
      order.delivery_address || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `NAS_Orders_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Orders exported to CSV');
  };

  // Export orders to printable format
  const exportOrdersPrint = () => {
    if (orders.length === 0) {
      toast.error('No orders to export');
      return;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Order History - ${new Date().toLocaleDateString()}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #16a34a; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; }
            th { background: #f9fafb; font-weight: 600; font-size: 12px; }
            td { font-size: 11px; }
            .status { padding: 2px 8px; border-radius: 10px; font-size: 10px; }
            .delivered { background: #dcfce7; color: #16a34a; }
            .pending { background: #fef3c7; color: #d97706; }
            .paid { background: #dcfce7; color: #16a34a; }
            .unpaid { background: #fee2e2; color: #dc2626; }
            .total-row { font-weight: bold; background: #f0fdf4; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>NAS Portal - Order History</h1>
          <p style="color: #6b7280; margin-bottom: 20px;">Generated on ${new Date().toLocaleString()}</p>
          
          <table>
            <thead>
              <tr>
                <th>Order Code</th>
                <th>Date</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              ${orders.map(order => `
                <tr>
                  <td><strong>${order.delivery_code}</strong></td>
                  <td>${new Date(order.created_at).toLocaleDateString()}</td>
                  <td>${order.items?.map(i => `${i.product_name} (${i.quantity})`).join(', ') || '-'}</td>
                  <td>₱${order.total_amount?.toLocaleString()}</td>
                  <td><span class="status ${order.status === 'delivered' ? 'delivered' : 'pending'}">${STATUS_CONFIG[order.status]?.label || order.status}</span></td>
                  <td><span class="status ${order.payment_status === 'paid' ? 'paid' : 'unpaid'}">${PAYMENT_STATUS_CONFIG[order.payment_status]?.label || order.payment_status}</span></td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3">Total (${orders.length} orders)</td>
                <td colspan="3">₱${orders.reduce((sum, o) => sum + (o.total_amount || 0), 0).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
          
          <p style="text-align: center; color: #9ca3af; margin-top: 40px; font-size: 11px;">
            NAS Portal - Nenen's Agrivet Supply
          </p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Order stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    inProgress: orders.filter(o => ['approved', 'released', 'out_for_delivery'].includes(o.status)).length,
    delivered: orders.filter(o => o.status === 'delivered').length,
  };

  return (
    <div className="p-4 pb-20 lg:pb-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">My Orders</h1>
          <p className="text-sm text-slate-500">{orders.length} total orders</p>
        </div>
        <div className="flex gap-2">
          {orders.length > 0 && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={exportOrdersCSV}
                data-testid="export-csv-btn"
              >
                <FileSpreadsheet size={16} className="mr-1" />
                <span className="hidden sm:inline">CSV</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={exportOrdersPrint}
                data-testid="export-print-btn"
              >
                <Download size={16} className="mr-1" />
                <span className="hidden sm:inline">Print</span>
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={loadOrders} disabled={isLoading}>
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="bg-white">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
            <p className="text-xs text-slate-500">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            <p className="text-xs text-slate-500">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
            <p className="text-xs text-slate-500">In Progress</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
            <p className="text-xs text-slate-500">Delivered</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by code or product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="released">Released</SelectItem>
            <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-green-600" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="p-10 text-center">
            <ClipboardList size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">
              {orders.length === 0 ? "You haven't placed any orders yet" : "No orders match your search"}
            </p>
            {orders.length === 0 && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => navigate('/nas-portal/catalog')}
              >
                <Package size={18} className="mr-2" />
                Browse Products
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map(order => {
            const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            const paymentConfig = PAYMENT_STATUS_CONFIG[order.payment_status] || PAYMENT_STATUS_CONFIG.unpaid;
            
            return (
              <Card 
                key={order.id} 
                className="bg-white cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => viewOrderDetails(order)}
                data-testid={`order-card-${order.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${statusConfig.color}`}>
                        <StatusIcon size={20} />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">{order.delivery_code}</p>
                        <p className="text-xs text-slate-500">{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">₱{order.total_amount?.toLocaleString()}</p>
                      <Badge className={paymentConfig.color}>{paymentConfig.label}</Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Package size={14} />
                      <span>{order.items?.length || 0} items</span>
                    </div>
                    <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                  </div>
                  
                  {/* Progress indicator for active orders */}
                  {['pending', 'approved', 'released', 'out_for_delivery'].includes(order.status) && (
                    <div className="mt-3 flex items-center gap-1">
                      {['pending', 'approved', 'released', 'out_for_delivery', 'delivered'].map((step, idx) => {
                        const stepOrder = ['pending', 'approved', 'released', 'out_for_delivery', 'delivered'];
                        const currentIdx = stepOrder.indexOf(order.status);
                        const isCompleted = idx <= currentIdx;
                        return (
                          <div 
                            key={step}
                            className={`h-1 flex-1 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-slate-200'}`}
                          />
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Order Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={20} />
              Order Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-bold text-lg">{selectedOrder.delivery_code}</p>
                  <p className="text-sm text-slate-500">{formatDate(selectedOrder.created_at)}</p>
                </div>
                <Badge className={STATUS_CONFIG[selectedOrder.status]?.color}>
                  {STATUS_CONFIG[selectedOrder.status]?.label}
                </Badge>
              </div>
              
              {/* Order Items */}
              <div>
                <h4 className="font-semibold mb-2">Order Items</h4>
                <div className="border rounded-lg divide-y">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="p-3 flex justify-between">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-slate-500">
                          {item.quantity} {item.unit} × ₱{item.unit_price?.toLocaleString()}
                        </p>
                      </div>
                      <p className="font-semibold">₱{item.total?.toLocaleString()}</p>
                    </div>
                  ))}
                  <div className="p-3 flex justify-between bg-green-50">
                    <p className="font-bold">Total</p>
                    <p className="font-bold text-green-600">₱{selectedOrder.total_amount?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              
              {/* Delivery Info */}
              <div>
                <h4 className="font-semibold mb-2">Delivery Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <MapPin size={16} className="text-slate-400 mt-0.5" />
                    <span>{selectedOrder.delivery_address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard size={16} className="text-slate-400" />
                    <span>{selectedOrder.payment_method === 'cod' ? 'Cash on Delivery' : 'Pay Later'}</span>
                    <Badge className={PAYMENT_STATUS_CONFIG[selectedOrder.payment_status]?.color}>
                      {PAYMENT_STATUS_CONFIG[selectedOrder.payment_status]?.label}
                    </Badge>
                  </div>
                  {selectedOrder.notes && (
                    <p className="text-slate-500 italic">Notes: {selectedOrder.notes}</p>
                  )}
                </div>
              </div>
              
              {/* Timeline */}
              <div>
                <h4 className="font-semibold mb-2">Order Timeline</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <div className="text-sm">
                      <p className="font-medium">Order Placed</p>
                      <p className="text-slate-500">{formatDate(selectedOrder.created_at)}</p>
                    </div>
                  </div>
                  
                  {selectedOrder.approved_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      <div className="text-sm">
                        <p className="font-medium">Approved by {selectedOrder.approved_by}</p>
                        <p className="text-slate-500">{formatDate(selectedOrder.approved_at)}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.released_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full" />
                      <div className="text-sm">
                        <p className="font-medium">Released by {selectedOrder.released_by}</p>
                        <p className="text-slate-500">{formatDate(selectedOrder.released_at)}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.driver && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-cyan-500 rounded-full" />
                      <div className="text-sm">
                        <p className="font-medium">Assigned to {selectedOrder.driver}</p>
                        <p className="text-slate-500">{formatDate(selectedOrder.driver_assigned_at)}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.delivered_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <div className="text-sm">
                        <p className="font-medium">Delivered</p>
                        <p className="text-slate-500">{formatDate(selectedOrder.delivered_at)}</p>
                        {selectedOrder.received_by && (
                          <p className="text-slate-500">Received by: {selectedOrder.received_by}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.status === 'rejected' && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-red-500 rounded-full" />
                      <div className="text-sm">
                        <p className="font-medium text-red-600">Rejected by {selectedOrder.rejected_by}</p>
                        <p className="text-slate-500">{formatDate(selectedOrder.rejected_at)}</p>
                        {selectedOrder.reject_reason && (
                          <p className="text-red-500">Reason: {selectedOrder.reject_reason}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Payment History */}
              {selectedOrder.payments?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Payment History</h4>
                  <div className="border rounded-lg divide-y">
                    {selectedOrder.payments.map((payment, idx) => (
                      <div key={idx} className="p-3 flex justify-between text-sm">
                        <div>
                          <p className="font-medium">₱{payment.amount?.toLocaleString()}</p>
                          <p className="text-slate-500">{payment.payment_method} • {formatDate(payment.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerOrders;
